import { type ClassValue, clsx } from 'clsx';
import { extendTailwindMerge } from 'tailwind-merge';
import { CONTROL_HEIGHT_CLASS } from './density';
import { TEXT_ROLE_CLASS_SUFFIXES } from './typography';

/** Custom `theme.extend.spacing` keys in `tailwind.config.ts`. */
const SPACING_TOKENS = [
  'touch',
  'touch-min',
  'control',
  'control-touch',
  'gutter',
  'gutter-compact',
  'card',
  'card-compact',
  'actions',
];

/**
 * Teach tailwind-merge the project's custom theme keys. Without this it cannot tell
 * that `min-h-touch-min` and `min-h-40` conflict, keeps both, and CSS order decides
 * which one wins instead of the caller's override.
 */
const twMerge = extendTailwindMerge<'text-role'>({
  extend: {
    theme: {
      spacing: SPACING_TOKENS,
      borderRadius: ['action', 'field', 'surface', 'menu-item', 'overlay', 'badge'],
    },
    classGroups: {
      // `text-body-m` etc. are role classes, not colours: without this, `text-foreground` would drop them.
      'text-role': [{ text: TEXT_ROLE_CLASS_SUFFIXES }],
      'font-weight': [{ font: ['body', 'menu', 'action', 'heading', 'label', 'strong'] }],
      'min-h': [{ 'min-h': SPACING_TOKENS }],
      'max-h': [{ 'max-h': SPACING_TOKENS }],
      'min-w': [{ 'min-w': SPACING_TOKENS }],
      'max-w': [{ 'max-w': SPACING_TOKENS }],
      shadow: [{ shadow: ['card', 'overlay', 'dialog'] }],
      duration: [{ duration: ['fast', 'standard', 'enter'] }],
      ease: [{ ease: ['motion', 'motion-enter', 'motion-in-out'] }],
      animate: [
        {
          animate: [
            'accordion-down',
            'accordion-up',
            'bell-wiggle',
            'badge-wiggle',
            'page-enter',
            'list-item-in',
            'list-item-out',
            'toast-in',
            'toast-out',
            'attention-wiggle',
          ],
        },
      ],
    },
  },
});

/** Desktop half of the control height pair, e.g. `lg:h-10`. */
const CONTROL_DESKTOP_HEIGHT = CONTROL_HEIGHT_CLASS.split(' ').find((token) => token.startsWith('lg:h-'));

/**
 * `h-auto` means "content height, not a control". tailwind-merge only replaces the
 * unprefixed half of the control height pair in `density.ts` (`h-11 lg:h-10`), so a
 * card-like Button with `h-auto` stayed 40px high at `lg` and its content overflowed.
 * A later `h-auto` drops the desktop half too. Fixed-height overrides (`h-8`) keep the
 * 40px desktop height, as before. Intentionally global: any `lg:h-10` before the last
 * `h-auto` in one `cn()` call is dropped; no caller writes that order on purpose.
 */
function dropControlHeightBeforeAuto(merged: string): string {
  if (!CONTROL_DESKTOP_HEIGHT || !merged.includes('h-auto')) return merged;
  const tokens = merged.split(' ');
  const autoIndex = tokens.lastIndexOf('h-auto');
  if (autoIndex < 0) return merged;
  return tokens
    .filter((token, index) => token !== CONTROL_DESKTOP_HEIGHT || index > autoIndex)
    .join(' ');
}

export function cn(...inputs: ClassValue[]) {
  return dropControlHeightBeforeAuto(twMerge(clsx(inputs)));
}

/**
 * Do not clip --focus-ring on this subtree. Box-shadow rings take no layout space,
 * so this must stay padding/margin-free — a gutter here shrinks the control next to
 * buttons and other unwrapped siblings. Put clipper padding on the overflow parent
 * instead (see `scrollableModalClass`, `TablePageToolbar`).
 */
export const focusRingSafeClass = 'overflow-visible';

/** Scrollable modal panel — inner padding keeps focus rings visible at scroll edges. */
export const scrollableModalClass = 'max-h-[90vh] overflow-y-auto p-1';

export function safeRedirect(to: string | null | undefined, fallback = '/dashboard'): string {
  if (!to || !to.startsWith('/') || to.startsWith('//')) return fallback;
  return to;
}
